/**
 * formatPublicDate — bilingual public-safe date renderer.
 *
 * Accepts:
 *   - ISO strings  ("2026-05-21T03:50:00+09:00", "2026-05-17", "2026-05-17T00:00:00")
 *   - Human strings ("May 17, 2026 (JST)")
 *   - falsy / invalid
 *
 * Returns a clean, locale-appropriate display ALWAYS rendered in
 * Asia/Tokyo (JST) regardless of the viewer's browser timezone:
 *   en  → "May 21, 2026"        (or "May 21, 2026 · 03:50 JST" if time is meaningful)
 *   ja  → "2026年5月21日"        (or "2026年5月21日 03:50 JST" if time is meaningful)
 *   invalid → "Date pending" / "日付確認中"
 *
 * Never emits raw ISO strings or "T00:00:00".
 *
 * Important: every component of the display is computed via
 * `Intl.DateTimeFormat` with `timeZone: "Asia/Tokyo"`, so a viewer in
 * any timezone — UTC, PST, JST — sees the SAME JST wall-clock time.
 * Previous implementation used `Date.prototype.getHours()` etc. which
 * silently used the browser's local timezone while still labeling the
 * output "JST" — producing off-by-one-day errors for non-JST viewers.
 */

const TZ = "Asia/Tokyo";

function parseLoose(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== "string") return null;
  // "May 17, 2026 (JST)" → strip parens, let Date parse
  const stripped = value.replace(/\s*\([^)]*\)\s*$/, "").trim();
  const d = new Date(stripped);
  if (!Number.isNaN(d.getTime())) return d;
  return null;
}

function hasMeaningfulTime(value) {
  if (typeof value !== "string") return false;
  // Skip "T00:00:00" — that's a placeholder, not a real time.
  if (/T00:00:00/.test(value)) return false;
  return /T\d{2}:\d{2}/.test(value);
}

/**
 * Extract Y/M/D/HH/MM as displayed in JST, regardless of the browser's
 * local timezone. Uses Intl.DateTimeFormat parts so the math is locale-
 * and DST-correct.
 */
function jstParts(d) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") === "24" ? "00" : get("hour"),
    minute: get("minute"),
  };
}

const MONTH_SHORT_EN = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function formatPublicDate(value, lang = "en") {
  const d = parseLoose(value);
  if (!d) {
    return lang === "ja" ? "日付確認中" : "Date pending";
  }
  const showTime = hasMeaningfulTime(value);
  const p = jstParts(d);
  // Strip leading zeros for human display (day/month numbers in JA,
  // day number in EN).
  const dayNum = String(parseInt(p.day, 10));
  const monthNum = String(parseInt(p.month, 10));
  const monthEn = MONTH_SHORT_EN[parseInt(p.month, 10) - 1] || p.month;

  if (lang === "ja") {
    if (!showTime) return `${p.year}年${monthNum}月${dayNum}日`;
    return `${p.year}年${monthNum}月${dayNum}日 ${p.hour}:${p.minute} JST`;
  }
  // English
  const base = `${monthEn} ${dayNum}, ${p.year}`;
  if (!showTime) return base;
  return `${base} · ${p.hour}:${p.minute} JST`;
}

/**
 * getSortTimestamp — returns a numeric ms-epoch for sort comparisons.
 * Invalid / missing values return -Infinity so they sort LAST when descending.
 */
export function getSortTimestamp(value) {
  const d = parseLoose(value);
  if (!d) return -Infinity;
  return d.getTime();
}
